import {
  ImageScaleView,
  ImageView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import { PetEntity } from 'src/game/logic/EntityManager';
import { createUIPet } from 'src/game/pet/pet.utils';
import sounds from 'src/lib/sounds';
import { PetSpeciesID } from 'src/replicant/pet';
import { petSpeciesAssets } from 'src/loadingGroups';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { PetState } from 'src/replicant/pet/pet.ruleset';

const hatImages = {
  normal: 'assets/ui/buddies/popupBaking/chef_hat_normal.png',
  mvp: 'assets/ui/buddies/popupBaking/chef_hat_mvp.png',
  loser: 'assets/ui/buddies/popupBaking/chef_hat_loser.png',
};

type BuddiesBakerPetOpts = ViewOpts & {
  onTapPet?: () => void;
};

export class BuddiesBakerPet extends View {
  private restingHat: ImageScaleView;
  private hatImage = hatImages.normal;
  private species?: PetSpeciesID;
  private pet?: PetEntity;
  private showedUp = false;
  private speciesLoaded?: PetSpeciesID;

  constructor(opts?: BuddiesBakerPetOpts) {
    super({
      width: 120,
      height: 120,
      centerOnOrigin: true,
      canHandleEvents: !!opts?.onTapPet,
      ...opts,
    });

    if (opts?.onTapPet) {
      this.onInputStart = opts.onTapPet;
    }

    this.restingHat = new ImageScaleView({
      superview: this,
      image: this.hatImage,
      scaleMethod: 'contain',
      width: 170,
      height: 170,
      x: this.style.width / 2,
      y: this.style.height - 40,
      centerOnOrigin: true,
      zIndex: 1,
    });
  }

  private updateHatImage(args: { mvp: boolean; eligible: boolean }) {
    if (args.mvp) {
      this.hatImage = hatImages.mvp;
    } else if (args.eligible) {
      this.hatImage = hatImages.normal;
    } else {
      this.hatImage = hatImages.loser;
    }
    this.restingHat.setImage(this.hatImage);
    this.updateHat();
  }

  public setPet(species: PetSpeciesID) {
    this.species = species;
    this.pet = createUIPet('normal', species);
    this.pet.updateOpts({
      superview: this,
      scale: 0.6,
      x: this.style.width / 2,
      y: this.style.height / 2,
      visible: false,
    });
  }

  public clearPet() {
    this.pet?.removeFromSuperview();
    this.pet = undefined;
    this.species = undefined;
    this.showedUp = false;
    this.restingHat.updateOpts({
      superview: this,
      x: this.style.width / 2,
      y: this.style.height - 40,
      visible: true,
    });
  }

  public updateState(args: {
    mvp: boolean;
    eligible: boolean;
    eventEnded: boolean;
  }) {
    if (!this.pet || !this.species) return;

    this.updateHatImage(args);

    let state: PetState;
    if (args.mvp || args.eligible) {
      state = 'happy';
    } else {
      state = args.eventEnded ? 'very_sad' : 'sad';
    }

    const animations = petAnimationNames[this.species][state];
    const animName =
      typeof animations === 'string' ? animations : animations.idle;
    this.pet.setAnimation(animName);
  }

  private async loadPetAssets() {
    const species = this.species;
    if (!species || this.speciesLoaded === species) return;
    await petSpeciesAssets[species].load();
    if (this.species === species) {
      this.speciesLoaded = species;
    }
  }

  private isLoaded() {
    return this.species && this.speciesLoaded === this.species;
  }

  public async animateShowUp() {
    if (!this.pet || !this.species || this.showedUp) return;
    this.showedUp = true;

    if (this.isLoaded()) {
      this.restingHat.hide();
      this.pet.updateOpts({
        y: this.style.height / 2,
        visible: true,
      });
      this.updateHat();
      return;
    }

    // Wait for pet animation assets to load
    await this.loadPetAssets();

    // Hide resting hat and attach via accessory system
    this.restingHat.hide();
    this.updateHat();

    // Hide pet behind mask
    this.pet.updateOpts({
      y: this.style.height * 2,
      visible: true,
    });

    sounds.sfx.play('tapPet');

    // Animate the pet show up with the hat
    await animate(this.pet)
      .now({ y: this.style.height / 2 }, 500, animate.easeOutBack)
      .toPromise();
  }

  private updateHat() {
    if (!this.pet) return;

    const flipX = this.isHatSlotFlipped();
    this.pet.petClip.addViewSubstitution(
      'placeholder_hat',
      new ImageView({
        image: this.hatImage,
        width: 360,
        height: 320,
        centerOnOrigin: true,
        flipX,
      }),
    );
  }

  private isHatSlotFlipped(): boolean {
    if (!this.pet) return false;
    const { timeline, frame } = this.pet.petClip;
    if (!timeline?.[frame]) return false;

    for (const child of timeline[frame]) {
      if (child.libraryID === 'placeholder_hat') {
        return child.transform.a < 0;
      }
    }
    return false;
  }
}
